import * as React from 'react';

import { cn } from '@/shared/lib/cn';

export interface InputProps extends React.ComponentPropsWithoutRef<'input'> {
  /** Element rendered at the start of the field (icon, prefix). */
  startAdornment?: React.ReactNode;
  /** Element rendered at the end of the field (unit, action). */
  endAdornment?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type = 'text', startAdornment, endAdornment, ...props },
  ref,
) {
  const field = (
    <input
      ref={ref}
      type={type}
      data-slot="input"
      className={cn(
        'border-input bg-card placeholder:text-muted-foreground h-11 w-full rounded-lg border px-3.5 text-sm transition-[border-color,box-shadow] outline-none',
        'focus-visible:border-ring focus-visible:ring-ring/25 focus-visible:ring-2',
        'disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-60',
        'aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/25',
        startAdornment && 'ps-10',
        endAdornment && 'pe-10',
        className,
      )}
      {...props}
    />
  );

  if (!startAdornment && !endAdornment) return field;

  return (
    <div className="relative w-full">
      {startAdornment ? (
        <span className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex w-10 items-center justify-center [&_svg]:size-4">
          {startAdornment}
        </span>
      ) : null}
      {field}
      {endAdornment ? (
        <span className="text-muted-foreground absolute inset-y-0 end-0 flex w-10 items-center justify-center text-xs [&_svg]:size-4">
          {endAdornment}
        </span>
      ) : null}
    </div>
  );
});

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentPropsWithoutRef<'textarea'>
>(function Textarea({ className, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      data-slot="textarea"
      className={cn(
        'border-input bg-card placeholder:text-muted-foreground min-h-20 w-full resize-y rounded-lg border px-3.5 py-2.5 text-sm transition-[border-color,box-shadow] outline-none',
        'focus-visible:border-ring focus-visible:ring-ring/25 focus-visible:ring-2',
        'disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-60',
        'aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/25',
        className,
      )}
      {...props}
    />
  );
});
